import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { Button, Input, Toast } from 'antd-mobile';
import { Header } from '../components/Header';
import { t } from '../../../i18n';
import { TmsPageState } from '../types/tms';
import { TmsTaskCardItem } from '../components/TmsTaskCardItem';
import { TmsWaypointStepIndicator } from './components/TmsWaypointStepIndicator';
import { TmsCountedInput } from './components/TmsCountedInput';
import MultiPhotoGalleryView from '../components/MultiPhotoGalleryView';
import { getBottomNavBarContainerStyle } from '../../../utils/deviceUtils';
import { PdaPath } from '../../../enums/PdaNavigatePathEnum';
import { TmsOperationSourceEnum, TmsTaskWaypointNodeType } from '../../../enums/TmsEnum';
import { pdaOpenCamera, startPrepareCameraLocation, FirstPhotoWatermarkLocation } from '../../../utils/appRuntime';
import { useSaveTmsTaskWaypointLocationInfo } from './hooks/useSaveTmsTaskWaypointLocationInfo';
import { getCurrentServerTimeMillis } from '../../../utils/pdaCameraUtils';
import { GET_ACCOUNT_BIND_WAREHOUSE, GetAccountBindWarehouseResponse } from '../../../graphql/queries/warehouseQueries';
import {
  GET_TMS_TASK_WAYPOINT_BOL_INFO,
  GetTmsTaskWaypointBolInfoResponse,
  GET_NEEDS_TMS_UNLOADING_OPERATION,
  NeedsTmsUnloadingOperationResponse,
  SAVE_TMS_UNLOADING_DETAIL_INFO
} from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';
import { useEffect, useState } from 'react';
import { fileUpload } from '../../../utils/storageUtils';
import { base64ToFile } from '../../../services/DeliveryDataService';

const kMaxBolPhotoLength = 3;
const kMinBolPhotoLength = 1;

export default function TmsUploadBolPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { backRoute, ...taskData } = (location.state as TmsPageState) || {};

  const taskId = taskData?.taskId ? parseInt(taskData.taskId) : NaN;
  const taskWayPointId = taskData?.taskWaypointId ? parseInt(taskData.taskWaypointId) : NaN;
  const isValidTaskId = !isNaN(taskId) && !isNaN(taskWayPointId);

  const apolloClient = useApolloClient();
  const { data: warehouseData } = useQuery<GetAccountBindWarehouseResponse>(GET_ACCOUNT_BIND_WAREHOUSE);
  const { data: bolInfoData } = useQuery<GetTmsTaskWaypointBolInfoResponse>(GET_TMS_TASK_WAYPOINT_BOL_INFO, {
    variables: { taskId: isValidTaskId ? taskId.toString() : '' },
    skip: !isValidTaskId
  });

  const { data: unloadingOperationData } = useQuery<NeedsTmsUnloadingOperationResponse>(
    GET_NEEDS_TMS_UNLOADING_OPERATION,
    {
      variables: { taskId, taskWayPointId },
      skip: !isValidTaskId || !taskWayPointId
    }
  );

  const [saveTmsUnloadingDetailInfo, { loading: submitLoading }] = useMutation<{ saveTmsUnloadingDetailInfo: boolean }>(
    SAVE_TMS_UNLOADING_DETAIL_INFO
  );

  const [bolRemark, setBolRemark] = useState('');
  const [bolPhotos, setBolPhotos] = useState<string[]>([]);
  const [bolPhotoKeysFromServer, setBolPhotoKeysFromServer] = useState<string[]>([]);
  const [firstPhotoLocation, setFirstPhotoLocation] = useState<FirstPhotoWatermarkLocation | null>(null);
  const { save: saveTmsTaskWaypointLocationInfo } = useSaveTmsTaskWaypointLocationInfo();

  useEffect(() => {
    startPrepareCameraLocation();
  }, []);

  useEffect(() => {
    if (!bolInfoData?.getTmsTaskWaypointsByTaskId) return;
    const waypoint = bolInfoData.getTmsTaskWaypointsByTaskId.find(wp => Number(wp.id) === taskWayPointId);
    if (!waypoint) return;
    if (typeof waypoint.bolRemark === 'string') setBolRemark(waypoint.bolRemark);
    if (Array.isArray(waypoint.bolPhotosJson)) setBolPhotoKeysFromServer(waypoint.bolPhotosJson);
    if (Array.isArray(waypoint.bolPhotosUrlsJson) && waypoint.bolPhotosUrlsJson.length > 0) {
      setBolPhotos(waypoint.bolPhotosUrlsJson);
    }
  }, [bolInfoData, taskWayPointId]);

  const handleRemarkChange = (val: string) => setBolRemark(val.slice(0, 50));

  const handleDeletePhoto = (index: number) => {
    setBolPhotos(prev => prev.filter((_, i) => i !== index));
    if (index < bolPhotoKeysFromServer.length) {
      setBolPhotoKeysFromServer(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleBurstCapture = async () => {
    const currentCount = bolPhotos.length;
    const remaining = Math.max(0, kMaxBolPhotoLength - currentCount);
    const minSelection = currentCount >= kMinBolPhotoLength ? 0 : kMinBolPhotoLength - currentCount;
    const photoTitles = [
      t('pda.pages.tms.arrival.firstPhoto') || '第一张照片',
      t('pda.pages.tms.arrival.secondPhoto') || '第二张照片',
      t('pda.pages.tms.arrival.thirdPhoto') || '第三张照片'
    ];
    const serverTimeMillis = await getCurrentServerTimeMillis(apolloClient);
    await pdaOpenCamera(
      remaining,
      minSelection,
      t('pda.pages.tms.arrival.photoTips') || '',
      photoTitles,
      t('pda.pages.tms.arrival.complete') || '',
      async (newPhotos: string[], loc?: FirstPhotoWatermarkLocation | null) => {
        if (newPhotos?.length > 0) setBolPhotos(prev => [...prev, ...newPhotos]);
        if (loc != null) setFirstPhotoLocation(loc);
      },
      {
        enableWatermark: true,
        ianaTimeZone: warehouseData?.warehouse?.timezoneLocation,
        serverTimeMillis
      }
    );
  };

  const handlePreviousStep = () => {
    navigate(PdaPath.PDA_TMS_UNLOADING_DETAIL, { state: { backRoute, ...taskData } });
  };

  const handleSubmit = async () => {
    const photoCount = bolPhotos.length;
    if (photoCount < kMinBolPhotoLength || photoCount > kMaxBolPhotoLength) {
      Toast.show({
        content: t('pda.pages.uploadBol.photoCountRequired') || '请上传1～3张BOL照片',
        position: 'center',
        duration: 2000
      });
      return;
    }

    try {
      let bolPhotoKeys: string[] = [...bolPhotoKeysFromServer];
      const newBase64Photos = bolPhotos.filter(p => p && !p.startsWith('http://') && !p.startsWith('https://'));
      if (newBase64Photos.length > 0) {
        const newKeys = await Promise.all(
          newBase64Photos.map(async (base64Photo, index) => {
            const formatted = base64Photo.startsWith('data:') ? base64Photo : `data:image/jpeg;base64,${base64Photo}`;
            const file = await base64ToFile(formatted, `tms_bol_${Date.now()}_${index}.jpg`);
            const result = await fileUpload(file, 'tms_bol_photo');
            return result.objectKey;
          })
        );
        bolPhotoKeys = [...bolPhotoKeys, ...newKeys];
      }

      await saveTmsTaskWaypointLocationInfo(taskId, taskWayPointId, TmsTaskWaypointNodeType.UNLOADING, firstPhotoLocation);

      const { data } = await saveTmsUnloadingDetailInfo({
        variables: {
          input: {
            taskId: taskId,
            taskWaypointId: taskWayPointId,
            items: [],
            bolRemark: bolRemark.trim() || null,
            bolPhotosJson: bolPhotoKeys,
            operationSource: TmsOperationSourceEnum.PDA,
          }
        }
      });

      if (data?.saveTmsUnloadingDetailInfo) {
        Toast.show({
          content: t('pda.common.submitSuccess') || '提交成功',
          position: 'center',
          duration: 2000
        });
        if (backRoute) navigate(backRoute);
        else navigate(-1);
      } else {
        throw new Error(t('pda.common.submitFailed') || '提交失败');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      Toast.show({
        content: `${t('pda.common.submitFailed') || '提交失败'}: ${msg}`,
        position: 'center',
        duration: 2000
      });
    }
  };

  if (!taskData) return null;

  return (
    <div className="w-full h-full flex flex-col gap-2 pt-2 bg-gray-50">
      <Header title={t('pda.pages.unloadingDetail.step2') || '上传BOL'} backRoute={backRoute} />
      <div className="p-3 flex flex-col gap-4 flex-1 overflow-y-auto pb-24">
        {unloadingOperationData?.needsTmsUnloadingOperation && (
          <TmsWaypointStepIndicator currentStep={2} variant="unloading" />
        )}
        {taskData.task && <TmsTaskCardItem task={taskData.task} showButtons={false} />}
        <div className="bg-white rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <div className="text-base font-medium text-gray-700">
            {t('pda.pages.tms.arrival.remark') || '备注'}
          </div>
          <TmsCountedInput
            value={bolRemark}
            onChange={handleRemarkChange}
            placeholder={t('pda.pages.tms.arrival.remarkPlaceholder') as string}
          />
          <div className="text-base font-medium text-gray-700">
            {t('pda.pages.uploadBol.bolPhotosTitle') || 'BOL照片 (1~3张)'}
          </div>
          <MultiPhotoGalleryView
            photos={bolPhotos}
            onDelete={handleDeletePhoto}
            onNativeBurstCapture={handleBurstCapture}
            maxDisplayCount={kMaxBolPhotoLength}
          />
        </div>
      </div>
      <div {...getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20')}>
        <div className="flex gap-2">
          {unloadingOperationData?.needsTmsUnloadingOperation && (
            <Button block color="default" onClick={handlePreviousStep} style={{ flex: 1 }}>
              {t('pda.common.previousStep') || '上一步'}
            </Button>
          )}
          <Button
            block
            color="primary"
            loading={submitLoading}
            onClick={handleSubmit}
            disabled={bolPhotos.length < kMinBolPhotoLength || bolPhotos.length > kMaxBolPhotoLength}
            style={{ flex: 1 }}
          >
            {t('pda.common.submit') || '提交'}
          </Button>
        </div>
      </div>
    </div>
  );
}
